import { apiClient } from './apiClient';
import { HardwareTypeSchema } from '../types/hardwareSchema';

export interface ValidTypeOptions {
  hardware_types: string[];
}

export interface HardwareType {
  id: number;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  ref_anep?: string[];
  reboot_payload?: string;
  is_default: boolean;
  is_active: boolean;
  device_count: number;
  created_at: string;
  updated_at: string;
}

export interface CustomFieldDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean';
  default: string | number | boolean;
  group?: string;
  notes?: string;
}

export interface HardwareTypeCreate {
  code: string;
  name: string;
  description?: string;
  icon?: string;
  ref_anep?: string[];
  reboot_payload?: string;
  client_id: string;
}

export interface HardwareTypeUpdate {
  code?: string;
  name?: string;
  description?: string;
  icon?: string;
  ref_anep?: string[];
  reboot_payload?: string;
  is_active?: boolean;
}

// Model Reference interfaces
export interface ModelReference {
  id: number;
  hardware_type_id: number;
  code: string;
  name?: string;
  description?: string;
  display_order: number;
  is_active: boolean;
  client_ids?: string[];  // Multi-client support
  device_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ModelReferenceCreate {
  code: string;
  name?: string;
  description?: string;
  display_order?: number;
  client_ids?: string[];  // Multi-client support
}

export interface ModelReferenceUpdate {
  code?: string;
  name?: string;
  description?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface ModelReferenceListResponse {
  items: ModelReference[];
  total: number;
}

// Field Grouping interfaces
export interface FieldGrouping {
  id: number;
  code: string;
  name: string;
  description?: string;
  display_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface FieldGroupingCreate {
  code: string;
  name: string;
  description?: string;
  display_order?: number;
}

export interface FieldGroupingUpdate {
  code?: string;
  name?: string;
  description?: string;
  display_order?: number;
}

// Field Definition interfaces
//
// Note on `select` vs `dropdown`:
//   - The backend's Pydantic FieldDefinitionCreate model accepts 'select'
//     as the dropdown value type (see provisioningv2 app/models/field_definition.py).
//   - Earlier frontend code used 'dropdown' as a display synonym; both
//     remain in the union for backward compat with local-only call sites
//     (custom-field editors, rendering helpers) but the **hardware type
//     Add Field form** must always send 'select' so the backend accepts it.
//   - 'email', 'ip_address', 'json' are frontend-only rendering hints and
//     are NOT valid for /v2/hardware-types/{id}/field-defs — they'll 422
//     if sent to that endpoint. Use them only for in-browser custom fields.
export type ValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'password'
  | 'url'
  | 'hostname'
  | 'email'
  | 'ip_address'
  | 'port'
  | 'select'
  | 'dropdown'
  | 'json'
  | 'array';

export interface FieldDefinition {
  id: number;
  config_type_id?: number;
  hardware_type_id?: number;
  grouping_id?: number;
  grouping_code?: string;
  grouping_name?: string;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string; // JSON string of dropdown options
  display_order: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  grouping?: FieldGrouping;
}

// Hardware-type specific field definitions
export interface HardwareFieldDefinition {
  id: number;
  hardware_type_id: number;
  grouping_id?: number;
  grouping_code?: string;
  grouping_name?: string;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string;
  display_order: number;
  is_system: boolean;
  expose_on_wire?: boolean;
  wire_root?: boolean;
  created_at: string;
  updated_at: string;
}

export interface HardwareFieldDefinitionCreate {
  grouping_id?: number;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required?: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string;
  display_order?: number;
  expose_on_wire?: boolean;
  wire_root?: boolean;
}

export interface HardwareFieldDefinitionUpdate {
  grouping_id?: number;
  display_name?: string;
  value_type?: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required?: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string;
  display_order?: number;
  expose_on_wire?: boolean;
  wire_root?: boolean;
}

export interface HardwareFieldDefinitionListResponse {
  items: HardwareFieldDefinition[];
  total: number;
  hardware_type_id: number;
  hardware_type_code: string;
}

// Bulk-apply input: a plain field object. All fields except field_name,
// display_name, and value_type are optional. default_value / options accept
// any scalar/array and will be stringified for the backend.
export interface HardwareFieldDefinitionInput {
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: unknown;
  placeholder?: string;
  help_text?: string;
  is_required?: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: unknown;
  display_order?: number;
  grouping_id?: number;
  expose_on_wire?: boolean;
  wire_root?: boolean;
}

export interface ApplySchemaResult {
  created: string[];   // field_names created
  updated: string[];   // field_names updated
  deleted: string[];   // field_names deleted (only if deleteMissing=true)
  skipped: string[];   // field_names skipped (e.g. system fields not force-deleted)
  errors: Array<{ field_name: string; error: string }>;
}

export interface ApplySchemaOptions {
  deleteMissing?: boolean;
  forceDeleteSystem?: boolean;
}

export interface FieldImpactResponse {
  field_id?: number;
  field_name?: string;
  device_count: number;
  template_count: number;
  can_delete: boolean;
  warning_message?: string;
}

export interface FieldDefinitionCreate {
  grouping_id?: number;
  field_name: string;
  display_name: string;
  value_type: ValueType;
  default_value?: string;
  placeholder?: string;
  help_text?: string;
  is_required?: boolean;
  validation_regex?: string;
  min_value?: number;
  max_value?: number;
  options?: string;
  display_order?: number;
}

class TypesService {
  // Cache keyed by include_inactive flag so the active-only and
  // all-rows variants don't trample each other.
  private validOptionsCache: Map<boolean, ValidTypeOptions> = new Map();
  private hardwareTypesCache: HardwareType[] = [];
  private lastFetch: Map<boolean, number> = new Map();
  private cacheDuration: number = 60000; // 1 minute

  async getValidOptions(
    includeInactive: boolean = false,
    forceRefresh: boolean = false,
  ): Promise<ValidTypeOptions> {
    const now = Date.now();
    const cached = this.validOptionsCache.get(includeInactive);
    const stamp = this.lastFetch.get(includeInactive) ?? 0;
    if (!forceRefresh && cached && now - stamp < this.cacheDuration) {
      return cached;
    }

    try {
      const url = includeInactive
        ? '/v2/types/valid-options?include_inactive=true'
        : '/v2/types/valid-options';
      const options = await apiClient.get(url);
      this.validOptionsCache.set(includeInactive, options);
      this.lastFetch.set(includeInactive, now);
      return options;
    } catch (error) {
      console.error('Failed to fetch valid type options:', error);
      // Return defaults if API fails
      return {
        hardware_types: ['UNKN', 'PGA', 'P4GU', 'IOTL', 'TMS2']
      };
    }
  }

  // ============================================================================
  // Hardware Types
  // ============================================================================

  async getHardwareTypes(includeInactive: boolean = false): Promise<HardwareType[]> {
    try {
      const url = includeInactive
        ? '/v2/hardware-types?include_inactive=true'
        : '/v2/hardware-types';
      const response = await apiClient.get(url);
      // Handle both array and paginated response formats
      const types = Array.isArray(response) ? response : (response.items || []);
      this.hardwareTypesCache = types;
      return types;
    } catch (error) {
      console.error('Failed to fetch hardware types:', error);
      return [];
    }
  }

  async getHardwareType(id: number): Promise<HardwareType | null> {
    try {
      return await apiClient.get(`/v2/hardware-types/${id}`);
    } catch (error) {
      console.error('Failed to fetch hardware type:', error);
      return null;
    }
  }

  async createHardwareType(data: HardwareTypeCreate): Promise<HardwareType> {
    const result = await apiClient.post('/v2/hardware-types', data);
    this.clearCache();
    return result;
  }

  async updateHardwareType(id: number, data: HardwareTypeUpdate): Promise<HardwareType> {
    const result = await apiClient.patch(`/v2/hardware-types/${id}`, data);
    this.clearCache();
    return result;
  }

  async deleteHardwareType(id: number): Promise<{ message: string }> {
    const result = await apiClient.delete(`/v2/hardware-types/${id}`);
    this.clearCache();
    return result;
  }

  /**
   * Fetch the JSON schema + default values for a hardware type.
   * Returns null on error so callers can render an empty state without
   * breaking the surrounding page.
   */
  async getHardwareTypeSchema(code: string): Promise<HardwareTypeSchema | null> {
    try {
      return await apiClient.get(`/v2/hardware-types/${code}/schema`);
    } catch (error) {
      console.error('Failed to fetch hardware type schema:', error);
      return null;
    }
  }

  // ============================================================================
  // Model References
  // ============================================================================

  async getModelReferences(hardwareTypeId: number, includeInactive: boolean = false): Promise<ModelReference[]> {
    try {
      const url = includeInactive
        ? `/v2/hardware-types/${hardwareTypeId}/model-refs?include_inactive=true`
        : `/v2/hardware-types/${hardwareTypeId}/model-refs`;
      const response = await apiClient.get(url);
      return response.items || [];
    } catch (error) {
      console.error('Failed to fetch model references:', error);
      return [];
    }
  }

  async createModelReference(hardwareTypeId: number, data: ModelReferenceCreate): Promise<ModelReference> {
    return await apiClient.post(`/v2/hardware-types/${hardwareTypeId}/model-refs`, data);
  }

  async updateModelReference(id: number, data: ModelReferenceUpdate): Promise<ModelReference> {
    return await apiClient.patch(`/v2/model-refs/${id}`, data);
  }

  async deleteModelReference(id: number): Promise<{ message: string }> {
    return await apiClient.delete(`/v2/model-refs/${id}`);
  }

  async reorderModelReferences(hardwareTypeId: number, order: number[]): Promise<ModelReferenceListResponse> {
    return await apiClient.put(`/v2/hardware-types/${hardwareTypeId}/model-refs/order`, { order });
  }

  // Model Reference Client Management
  async addModelReferenceClient(refId: number, clientId: string): Promise<{ message: string; client_ids: string[] }> {
    return await apiClient.post(`/v2/model-refs/${refId}/clients/${clientId}`, {});
  }

  async removeModelReferenceClient(refId: number, clientId: string): Promise<{ message: string; client_ids: string[] }> {
    return await apiClient.delete(`/v2/model-refs/${refId}/clients/${clientId}`);
  }

  async getModelReferenceClients(refId: number): Promise<string[]> {
    const result = await apiClient.get(`/v2/model-refs/${refId}/clients`);
    return result.client_ids || [];
  }

  // ============================================================================
  // Hardware Field Definitions
  // ============================================================================

  async getHardwareFieldDefinitions(hardwareTypeId: number, groupingId?: number): Promise<HardwareFieldDefinitionListResponse> {
    try {
      let url = `/v2/hardware-types/${hardwareTypeId}/field-defs`;
      if (groupingId !== undefined) {
        url += `?grouping_id=${groupingId}`;
      }
      const response = await apiClient.get(url);
      return {
        items: response.items || [],
        total: response.total || 0,
        hardware_type_id: response.hardware_type_id,
        hardware_type_code: response.hardware_type_code
      };
    } catch (error) {
      console.error('Failed to fetch hardware field definitions:', error);
      return { items: [], total: 0, hardware_type_id: hardwareTypeId, hardware_type_code: '' };
    }
  }

  async createHardwareFieldDefinition(hardwareTypeId: number, data: HardwareFieldDefinitionCreate): Promise<HardwareFieldDefinition> {
    return await apiClient.post(`/v2/hardware-types/${hardwareTypeId}/field-defs`, data);
  }

  async updateHardwareFieldDefinition(id: number, data: HardwareFieldDefinitionUpdate): Promise<HardwareFieldDefinition> {
    return await apiClient.patch(`/v2/field-defs/${id}`, data);
  }

  async deleteHardwareFieldDefinition(id: number, force: boolean = false): Promise<{ message: string }> {
    const url = force ? `/v2/field-defs/${id}?force=true` : `/v2/field-defs/${id}`;
    return await apiClient.delete(url);
  }

  async checkFieldImpact(hardwareTypeId: number, fieldId?: number): Promise<FieldImpactResponse> {
    try {
      let url = `/v2/hardware-types/${hardwareTypeId}/field-defs/check-impact`;
      if (fieldId !== undefined) {
        url += `?field_id=${fieldId}`;
      }
      return await apiClient.get(url);
    } catch (error) {
      console.error('Failed to check field impact:', error);
      return { device_count: 0, template_count: 0, can_delete: true };
    }
  }

  /**
   * Bulk-apply a full set of field definitions to a hardware type.
   *
   * Upserts by field_name: an input field whose field_name already exists
   * on the hardware type is PATCHed; otherwise it is POSTed. When
   * deleteMissing=true, any existing field whose field_name is NOT in the
   * input list is DELETEd (system fields are skipped unless forceDeleteSystem
   * is also true).
   *
   * default_value and options are coerced to strings before sending since
   * the backend stores them as Text/JSON-string respectively.
   *
   * Returns a summary of what was created/updated/deleted/skipped and any
   * per-field errors. Errors on individual fields do not abort the whole
   * operation — the method makes a best-effort pass and reports partial
   * success.
   */
  async applyHardwareSchema(
    hardwareTypeId: number,
    input: HardwareFieldDefinitionInput[],
    options: ApplySchemaOptions = {}
  ): Promise<ApplySchemaResult> {
    const { deleteMissing = false, forceDeleteSystem = false } = options;
    const result: ApplySchemaResult = {
      created: [],
      updated: [],
      deleted: [],
      skipped: [],
      errors: [],
    };

    // Load the current state so we can diff against it.
    const current = await this.getHardwareFieldDefinitions(hardwareTypeId);
    const byName = new Map<string, HardwareFieldDefinition>();
    for (const f of current.items) byName.set(f.field_name, f);

    // Normalise: strip undefined/empty, stringify default_value + options.
    const normalize = (f: HardwareFieldDefinitionInput): HardwareFieldDefinitionCreate => {
      const out: HardwareFieldDefinitionCreate = {
        field_name: f.field_name,
        display_name: f.display_name,
        value_type: f.value_type,
      };
      if (f.grouping_id !== undefined && f.grouping_id !== null) out.grouping_id = f.grouping_id;
      if (f.default_value !== undefined && f.default_value !== null) {
        out.default_value = typeof f.default_value === 'string'
          ? f.default_value
          : JSON.stringify(f.default_value);
      }
      if (f.placeholder) out.placeholder = f.placeholder;
      if (f.help_text) out.help_text = f.help_text;
      if (f.is_required !== undefined) out.is_required = !!f.is_required;
      if (f.validation_regex) out.validation_regex = f.validation_regex;
      if (f.min_value !== undefined && f.min_value !== null) out.min_value = f.min_value;
      if (f.max_value !== undefined && f.max_value !== null) out.max_value = f.max_value;
      if (f.options !== undefined && f.options !== null) {
        out.options = typeof f.options === 'string'
          ? f.options
          : JSON.stringify(f.options);
      }
      if (f.display_order !== undefined && f.display_order !== null) out.display_order = f.display_order;
      if (f.expose_on_wire !== undefined) out.expose_on_wire = !!f.expose_on_wire;
      if (f.wire_root !== undefined) out.wire_root = !!f.wire_root;
      return out;
    };

    // Upsert pass
    const inputNames = new Set<string>();
    for (const raw of input) {
      if (!raw || !raw.field_name) {
        result.errors.push({ field_name: raw?.field_name || '(unknown)', error: 'missing field_name' });
        continue;
      }
      inputNames.add(raw.field_name);
      const normalized = normalize(raw);
      const existing = byName.get(raw.field_name);
      try {
        if (existing) {
          // PATCH — omit field_name (can't be changed server-side)
          const { field_name: _omit, ...patch } = normalized;
          void _omit;
          await this.updateHardwareFieldDefinition(existing.id, patch as HardwareFieldDefinitionUpdate);
          result.updated.push(raw.field_name);
        } else {
          await this.createHardwareFieldDefinition(hardwareTypeId, normalized);
          result.created.push(raw.field_name);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        result.errors.push({ field_name: raw.field_name, error: message });
      }
    }

    // Deletion pass — only when deleteMissing is requested
    if (deleteMissing) {
      for (const [name, existing] of byName.entries()) {
        if (inputNames.has(name)) continue;
        if (existing.is_system && !forceDeleteSystem) {
          result.skipped.push(name);
          continue;
        }
        try {
          await this.deleteHardwareFieldDefinition(existing.id, existing.is_system);
          result.deleted.push(name);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          result.errors.push({ field_name: name, error: message });
        }
      }
    }

    return result;
  }

  // ============================================================================
  // Field Groupings
  // ============================================================================

  async getFieldGroupings(): Promise<FieldGrouping[]> {
    try {
      const response = await apiClient.get('/v2/field-groupings');
      return response.items || [];
    } catch (error) {
      console.error('Failed to fetch field groupings:', error);
      return [];
    }
  }

  async createFieldGrouping(data: FieldGroupingCreate): Promise<FieldGrouping> {
    return await apiClient.post('/v2/field-groupings', data);
  }

  async updateFieldGrouping(id: number, data: FieldGroupingUpdate): Promise<FieldGrouping> {
    return await apiClient.patch(`/v2/field-groupings/${id}`, data);
  }

  async deleteFieldGrouping(id: number): Promise<{ message: string }> {
    return await apiClient.delete(`/v2/field-groupings/${id}`);
  }

  // ============================================================================
  // Config Template Client Management
  // ============================================================================

  async addConfigTemplateClient(templateId: number, clientId: string): Promise<{ message: string; client_ids: string[] }> {
    return await apiClient.post(`/v2/config-templates/${templateId}/clients/${clientId}`, {});
  }

  async removeConfigTemplateClient(templateId: number, clientId: string): Promise<{ message: string; client_ids: string[] }> {
    return await apiClient.delete(`/v2/config-templates/${templateId}/clients/${clientId}`);
  }

  async getConfigTemplateClients(templateId: number): Promise<string[]> {
    const result = await apiClient.get(`/v2/config-templates/${templateId}/clients`);
    return result.client_ids || [];
  }

  // ============================================================================
  // Cache Management
  // ============================================================================

  getCachedHardwareTypes(): HardwareType[] {
    return this.hardwareTypesCache;
  }

  clearCache(): void {
    this.validOptionsCache.clear();
    this.hardwareTypesCache = [];
    this.lastFetch.clear();
  }
}

export const typesService = new TypesService();
